import { useCallback, useEffect, useState } from 'react'
import { com } from '@eidu/form'
import LoadingOverlay from '../../../../components/feedback/LoadingOverlay'
import PermissionError from '../../PermissionError'
import EntityFieldInputGroup from '../shared/EntityFieldInputGroup'
import PageLayout from '../shared/PageLayout'
import useEntityEdit from '../shared/useEntityEdit'
import LoginIdentifierField from '../../../../components/input/LoginIdentifierField'
import useHasGlobalPermissions from '../../../permission/useHasGlobalPermissions'
import emailLoginIdentifierKey = com.eidu.sharedlib.entity.emailLoginIdentifierKey
import phoneNumberLoginIdentifierKey = com.eidu.sharedlib.entity.phoneNumberLoginIdentifierKey
import PhoneNumber = com.eidu.sharedlib.user.PhoneNumber
import isRealMobilePhoneNumber = com.eidu.sharedlib.user.isRealMobilePhoneNumber
import GlobalAction = com.eidu.sharedlib.entity.permission.GlobalAction
import { KindEditPageProps } from '../KindPages'
import computeLabel from '../shared/computeLabel'

const isValidEmailOrEmpty = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return email === '' || emailRegex.test(email)
}

const isValidPhoneNumberOrEmpty = (phone: string): boolean => {
  const phoneNumber = PhoneNumber.fromString(phone)
  return phone === '' || !!(phoneNumber && isRealMobilePhoneNumber(phoneNumber))
}

const UserEditPage = ({ entity, entityId, entityType, entityTypes, entityRepository, loading }: KindEditPageProps) => {
  const {
    fields,
    updateField,
    submitting,
    hasAttemptedSubmit,
    valid,
    submitEntity,
    deleteEntity,
    hasPermissions,
    permissionsLoading,
    registerExternalValues,
    registerValidation,
  } = useEntityEdit({
    entity,
    entityId,
    entityType,
    entityRepository,
  })

  const [phone, setPhone] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [phoneError, setPhoneError] = useState<string | undefined>()
  const [emailError, setEmailError] = useState<string | undefined>()

  const { hasPermission } = useHasGlobalPermissions(GlobalAction.ManageAuthentication)
  const showAuthenticationInfo = hasPermission(GlobalAction.ManageAuthentication)

  useEffect(() => {
    if (entity) {
      const externalValues = entity.entity.externalValues?.asJsReadonlyMapView() ?? new Map()
      setPhone(externalValues.get(phoneNumberLoginIdentifierKey) || '')
      setEmail(externalValues.get(emailLoginIdentifierKey) || '')
    }
  }, [entity])

  useEffect(() => {
    if (!isValidEmailOrEmpty(email) && hasAttemptedSubmit) {
      setEmailError('Invalid email format')
    } else {
      setEmailError(undefined)
    }

    if (!isValidPhoneNumberOrEmpty(phone) && hasAttemptedSubmit) {
      setPhoneError('Invalid phone number format')
    } else {
      setPhoneError(undefined)
    }
  }, [email, phone, hasAttemptedSubmit])

  const validate = useCallback((): boolean => {
    let hasEmailError = false
    let hasPhoneError = false

    if (!isValidEmailOrEmpty(email)) {
      setEmailError('Invalid email format')
      hasEmailError = true
    }

    if (!isValidPhoneNumberOrEmpty(phone)) {
      setPhoneError('Invalid phone number format')
      hasPhoneError = true
    }

    return !(hasEmailError || hasPhoneError)
  }, [email, phone])

  useEffect(() => {
    registerValidation(validate)
  }, [registerValidation, validate])

  useEffect(() => {
    const externalValues = new Map()
    externalValues.set(phoneNumberLoginIdentifierKey, PhoneNumber.fromString(phone)?.normalized || null)
    externalValues.set(emailLoginIdentifierKey, email || null)
    registerExternalValues(externalValues)
  }, [registerExternalValues, phone, email])

  const loadingOverlayOpen = submitting

  if (!hasPermissions && !loading && !permissionsLoading) {
    return <PermissionError missingPermittedActions={[]} />
  }

  const label = computeLabel(entityType, fields)

  return (
    <>
      <PageLayout
        title={label ?? entityType.name}
        subtitle={label ? entityType.name : undefined}
        showCancel
        showSave
        showDelete={!!entity}
        saveDisabled={!valid}
        onSave={submitEntity}
        onDelete={deleteEntity}
      >
        <EntityFieldInputGroup
          entityType={entityType}
          entityTypes={entityTypes}
          fields={fields}
          updateField={updateField}
          loading={loading}
        />
        {showAuthenticationInfo && (
          <LoginIdentifierField
            phone={phone}
            setPhone={setPhone}
            email={email}
            setEmail={setEmail}
            phoneError={phoneError}
            emailError={emailError}
            disabled={loading}
          />
        )}
      </PageLayout>
      <LoadingOverlay isOpen={loadingOverlayOpen} />
    </>
  )
}

export default UserEditPage
